import { Fragment, useState, useEffect } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock,
  Plus, Trash2, Edit2, ExternalLink, Check,
  Users, Building, Flame, Settings, Target, Building2,
  Calendar, CreditCard, X, DollarSign, UserCheck, Percent,
  PieChart, Layers, ChevronRight, ChevronDown, RefreshCw, ArrowRightLeft, FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Slider } from "../../components/ui/slider";
import { Progress } from "../../components/ui/progress";
import VendorPaymentPlanEditor, {
  rescaleInstallments,
  derivePlanStatus,
  nextUnpaidInstallment,
  formatShortDate,
} from "./VendorPaymentPlanEditor";

// Allocation categories
const ALLOCATION_CATEGORIES = [
  { value: "visualization", label: "Visualization", color: "#3D6355" },
  { value: "marketing", label: "Marketing", color: "#2E7D55" },
  { value: "vexos", label: "VEXOS", color: "#3D6355" },
  { value: "other", label: "Other", color: "#565350" }
];

const HEALTH_STYLES = {
  healthy: { bg: "bg-emerald-500", text: "text-white", label: "Healthy", color: "#2E7D55" },
  caution: { bg: "bg-amber-500", text: "text-white", label: "Caution", color: "#B96A43" },
  near_breakeven: { bg: "bg-orange-500", text: "text-white", label: "Near B/E", color: "#B96A43" },
  loss: { bg: "bg-red-500", text: "text-white", label: "Loss", color: "#C24A2E" }
};

// How many labour rows a collapsed table shows. Thirty scheduled blocks is a
// wall of numbers that buries the panels under it; the first few are enough to
// see whose time is on this project, and the total below never collapses.
const LABOR_ROWS_SHOWN = 5;

// The "see more" foot of a capped table. Sits inside the table's border so it
// reads as the last row rather than a control floating under the card, and names
// how many rows are still hidden — "See more" alone doesn't say if that's two
// or two hundred.
function ShowMoreFooter({ hidden, expanded, onToggle, testId }) {
  if (hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-center gap-1.5 py-2 border-t text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
      aria-expanded={expanded}
      data-testid={testId}
    >
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
      {expanded ? "Show less" : `See ${hidden} more`}
    </button>
  );
}

// A cost with a payment plan is only as paid as its paid installments.
function costItemPaid(item) {
  if (item.has_payment_plan && item.installments?.length) {
    return item.installments.filter(i => i.paid).reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  }
  return item.payment_status === "paid" ? item.amount : 0;
}

function formatCurrency(value) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(1)}%`;
}

export default function ProgramCostingTab({ programId }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [assignmentBlocks, setAssignmentBlocks] = useState([]);
  // Overtime booked on the Artist Timeline — read-only here, edited there.
  const [otEntries, setOtEntries] = useState([]);
  // Both labour tables start capped at LABOR_ROWS_SHOWN.
  const [showAllBlocks, setShowAllBlocks] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [showAllOt, setShowAllOt] = useState(false);
  const [costItems, setCostItems] = useState([]);
  const [resources, setResources] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [config, setConfig] = useState(null);
  const [profitModel, setProfitModel] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Burn window
  const [burnWindow, setBurnWindow] = useState(7);
  
  // Dialogs
  const [showTimeLogDialog, setShowTimeLogDialog] = useState(false);
  const [showCostItemDialog, setShowCostItemDialog] = useState(false);
  const [showMarginDialog, setShowMarginDialog] = useState(false);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [editingTimeLog, setEditingTimeLog] = useState(null);
  const [editingCostItem, setEditingCostItem] = useState(null);
  const [editingCommission, setEditingCommission] = useState(null);
  
  // Form state
  const [timeLogForm, setTimeLogForm] = useState({
    resource_id: "", date: new Date().toISOString().split("T")[0], hours_logged: 1, work_type: "other", cost_category: "visualization", notes: ""
  });
  const [costItemForm, setCostItemForm] = useState({
    category: "misc", cost_category: "visualization", vendor_id: "", vendor_name: "", description: "", amount: 0,
    incurred_date: "", due_date: "", payment_status: "unpaid", invoice_link_url: "",
    has_payment_plan: false, installments: []
  });
  const [marginForm, setMarginForm] = useState({
    target_margin_mode: "fixed_percent",
    target_margin_percent: 75
  });
  const [commissionForm, setCommissionForm] = useState({
    user_id: "",
    description: "",
    amount: 0,
    commission_type: "referral",
    percentage: null,
    payment_status: "unpaid",
    paid_date: "",
    notes: ""
  });
  
  // Allocation state
  const [showAllocationDialog, setShowAllocationDialog] = useState(false);
  const [allocations, setAllocations] = useState([]);
  // What the linked quote says the split should be. Fetched when the dialog
  // opens, never applied on its own — see applyQuoteSplit.
  const [quoteSplit, setQuoteSplit] = useState(null);
  const [quoteSplitApplied, setQuoteSplitApplied] = useState(false);
  const [allocationForm, setAllocationForm] = useState([
    { category: "visualization", amount_type: "percent", amount_value: 100 },
    { category: "marketing", amount_type: "percent", amount_value: 0 },
    { category: "vexos", amount_type: "percent", amount_value: 0 },
    { category: "other", amount_type: "percent", amount_value: 0 }
  ]);
  const [savingAllocations, setSavingAllocations] = useState(false);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  
  // Impact preview
  const [impactPreview, setImpactPreview] = useState(null);

  // Reassign orphaned resource
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignOldId, setReassignOldId] = useState("");
  const [reassignNewId, setReassignNewId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const isAdmin = user?.role === "COO" || user?.role === "Admin";

  useEffect(() => {
    if (programId) {
      fetchAllData();
    }
  }, [programId, burnWindow]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, timeLogsRes, assignmentBlocksRes, otEntriesRes, costItemsRes, resourcesRes, vendorsRes, configRes, profitModelRes, commissionsRes, usersRes] = await Promise.all([
        apiClient.get(`/costing/program/${programId}/summary?burn_window=${burnWindow}`),
        apiClient.get(`/costing/time-logs?program_id=${programId}`),
        apiClient.get(`/costing/assignment-blocks?program_id=${programId}`),
        apiClient.get(`/ot-entries?program_id=${programId}`),
        apiClient.get(`/costing/cost-items?program_id=${programId}`),
        apiClient.get("/resources?resource_type=all"),
        apiClient.get("/vendors"),
        apiClient.get("/costing/config"),
        apiClient.get(`/costing/profit-model/${programId}`),
        apiClient.get(`/programs/${programId}/commissions`),
        apiClient.get("/users")
      ]);
      
      setSummary(summaryRes.data);
      setTimeLogs(timeLogsRes.data);
      setAssignmentBlocks(assignmentBlocksRes.data);
      setOtEntries(otEntriesRes.data || []);
      setCostItems(costItemsRes.data);
      setResources(resourcesRes.data);
      setVendors(vendorsRes.data);
      setConfig(configRes.data);
      setProfitModel(profitModelRes.data);
      setCommissions(commissionsRes.data);
      setUsers(usersRes.data);
      setMarginForm({
        target_margin_mode: "fixed_percent",
        target_margin_percent: profitModelRes.data?.target_margin_percent || 75
      });
    } catch (err) {
      toast.error("Failed to load costing data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await apiClient.get(`/costing/program/${programId}/summary?burn_window=${burnWindow}`);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to refresh summary");
    }
  };

  // Force sync with Artist Timeline
  const handleSyncFromTimeline = async () => {
    setSyncing(true);
    try {
      // Re-fetch all data including assignment blocks from timeline
      const [summaryRes, assignmentBlocksRes] = await Promise.all([
        apiClient.get(`/costing/program/${programId}/summary?burn_window=${burnWindow}&force_refresh=true`),
        apiClient.get(`/costing/assignment-blocks?program_id=${programId}`)
      ]);
      setSummary(summaryRes.data);
      setAssignmentBlocks(assignmentBlocksRes.data);
      toast.success("Synced with Artist Timeline");
    } catch (err) {
      toast.error("Failed to sync - try refreshing the page");
    } finally {
      setSyncing(false);
    }
  };

  // Reassign orphaned resource entries
  const openReassignDialog = (oldResourceId) => {
    setReassignOldId(oldResourceId);
    setReassignNewId("");
    setShowReassignDialog(true);
  };

  const handleReassignResource = async () => {
    if (!reassignOldId || !reassignNewId) return;
    setReassigning(true);
    try {
      const res = await apiClient.patch("/costing/reassign-resource", {
        old_resource_id: reassignOldId,
        new_resource_id: reassignNewId,
        program_id: programId
      });
      const d = res.data;
      toast.success(`Reassigned ${d.total_updated} entries to ${d.new_resource_name}`);
      setShowReassignDialog(false);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign resource");
    } finally {
      setReassigning(false);
    }
  };

  // Margin Settings
  const handleSaveMargin = async () => {
    try {
      await apiClient.post("/costing/profit-model", {
        program_id: programId,
        target_margin_mode: marginForm.target_margin_mode,
        target_margin_percent: marginForm.target_margin_percent
      });
      toast.success("Margin target updated");
      setShowMarginDialog(false);
      fetchAllData();
    } catch (err) {
      console.error("Update margin error:", err.response?.data);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : "Failed to update margin";
      toast.error(msg);
    }
  };

  // Helper to extract error message from API response
  const getErrorMessage = (err, fallback = "An error occurred") => {
    const detail = err.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors are arrays
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return fallback;
  };

  // Time Log CRUD
  const handleSaveTimeLog = async () => {
    try {
      if (editingTimeLog) {
        await apiClient.patch(`/costing/time-logs/${editingTimeLog.id}`, timeLogForm);
        toast.success("Time log updated");
      } else {
        await apiClient.post("/costing/time-logs", { ...timeLogForm, program_id: programId });
        toast.success("Time log added");
      }
      
      setShowTimeLogDialog(false);
      setEditingTimeLog(null);
      resetTimeLogForm();
      
      // Refresh data
      const res = await apiClient.get(`/costing/time-logs?program_id=${programId}`);
      setTimeLogs(res.data);
      fetchSummary();
    } catch (err) {
      console.error("Save time log error:", err.response?.data);
      toast.error(getErrorMessage(err, "Failed to save time log"));
    }
  };

  const handleDeleteTimeLog = async (id) => {
    if (!confirm("Delete this time log?")) return;
    try {
      await apiClient.delete(`/costing/time-logs/${id}`);
      toast.success("Time log deleted");
      setTimeLogs(timeLogs.filter(t => t.id !== id));
      fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete time log"));
    }
  };

  // Update assignment block category
  const handleUpdateBlockCategory = async (blockId, newCategory) => {
    try {
      await apiClient.patch(`/costing/assignment-blocks/${blockId}/category`, { 
        cost_category: newCategory 
      });
      toast.success("Category updated");
      // Update local state
      setAssignmentBlocks(prev => 
        prev.map(b => b.id === blockId ? { ...b, cost_category: newCategory } : b)
      );
      fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update category"));
    }
  };

  // Cost Item CRUD
  const handleSaveCostItem = async () => {
    try {
      if (editingCostItem) {
        await apiClient.patch(`/costing/cost-items/${editingCostItem.id}`, costItemForm);
        toast.success("Cost item updated");
      } else {
        await apiClient.post("/costing/cost-items", { ...costItemForm, program_id: programId });
        toast.success("Cost item added");
      }
      
      setShowCostItemDialog(false);
      setEditingCostItem(null);
      resetCostItemForm();
      setImpactPreview(null);
      
      // Refresh data
      const res = await apiClient.get(`/costing/cost-items?program_id=${programId}`);
      setCostItems(res.data);
      fetchSummary();
    } catch (err) {
      console.error("Save cost item error:", err.response?.data);
      toast.error(getErrorMessage(err, "Failed to save cost item"));
    }
  };

  const handleDeleteCostItem = async (id) => {
    if (!confirm("Delete this cost item?")) return;
    try {
      await apiClient.delete(`/costing/cost-items/${id}`);
      toast.success("Cost item deleted");
      setCostItems(costItems.filter(c => c.id !== id));
      fetchSummary();
    } catch (err) {
      toast.error("Failed to delete cost item");
    }
  };

  // On a payment plan the check pays the next installment, not the whole cost.
  const handleMarkPaid = async (item, installment = null) => {
    const todayStr = new Date().toISOString().split("T")[0];
    const target = item.has_payment_plan && item.installments?.length
      ? (installment || nextUnpaidInstallment(item.installments))
      : null;
    try {
      if (target) {
        await apiClient.patch(`/costing/cost-items/${item.id}`, {
          installments: item.installments.map(i =>
            i === target ? { ...i, paid: true, paid_date: todayStr } : i
          )
        });
        toast.success(`${target.label || "Payment"} marked paid`);
      } else {
        await apiClient.patch(`/costing/cost-items/${item.id}`, {
          payment_status: "paid",
          paid_date: todayStr
        });
        toast.success("Marked as paid");
      }
      const res = await apiClient.get(`/costing/cost-items?program_id=${programId}`);
      setCostItems(res.data);
      fetchSummary();
    } catch (err) {
      toast.error("Failed to update payment status");
    }
  };

  // Commission CRUD
  const handleSaveCommission = async () => {
    try {
      const payload = {
        ...commissionForm,
        program_id: programId,
        percentage: commissionForm.percentage || null,
        paid_date: commissionForm.paid_date || null,
        notes: commissionForm.notes || null
      };
      
      if (editingCommission) {
        await apiClient.patch(`/programs/${programId}/commissions/${editingCommission.id}`, payload);
        toast.success("Commission updated");
      } else {
        await apiClient.post(`/programs/${programId}/commissions`, payload);
        toast.success("Commission added");
      }
      
      setShowCommissionDialog(false);
      setEditingCommission(null);
      resetCommissionForm();
      
      // Refresh data
      const res = await apiClient.get(`/programs/${programId}/commissions`);
      setCommissions(res.data);
      fetchSummary();
    } catch (err) {
      console.error("Save commission error:", err.response?.data);
      toast.error(getErrorMessage(err, "Failed to save commission"));
    }
  };

  const handleDeleteCommission = async (id) => {
    if (!confirm("Delete this commission?")) return;
    try {
      await apiClient.delete(`/programs/${programId}/commissions/${id}`);
      toast.success("Commission deleted");
      setCommissions(commissions.filter(c => c.id !== id));
      fetchSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete commission"));
    }
  };

  const handleMarkCommissionPaid = async (commission) => {
    try {
      await apiClient.patch(`/programs/${programId}/commissions/${commission.id}`, {
        payment_status: "paid",
        paid_date: new Date().toISOString().split("T")[0]
      });
      toast.success("Commission marked as paid");
      const res = await apiClient.get(`/programs/${programId}/commissions`);
      setCommissions(res.data);
      fetchSummary();
    } catch (err) {
      toast.error("Failed to update commission status");
    }
  };

  const resetCommissionForm = () => {
    setCommissionForm({
      user_id: "",
      description: "",
      amount: 0,
      commission_type: "referral",
      percentage: null,
      payment_status: "unpaid",
      paid_date: "",
      notes: ""
    });
  };

  const openEditCommission = (commission) => {
    setEditingCommission(commission);
    setCommissionForm({
      user_id: commission.user_id,
      description: commission.description,
      amount: commission.amount,
      commission_type: commission.commission_type,
      percentage: commission.percentage || null,
      payment_status: commission.payment_status,
      paid_date: commission.paid_date || "",
      notes: commission.notes || ""
    });
    setShowCommissionDialog(true);
  };

  // Allocation handlers
  const openAllocationDialog = () => {
    // Initialize form from existing allocations
    const existingAllocations = summary?.allocations || [];
    const newForm = ALLOCATION_CATEGORIES.map(cat => {
      const existing = existingAllocations.find(a => a.category === cat.value);
      return {
        category: cat.value,
        amount_type: existing?.amount_type || "percent",
        amount_value: existing?.amount_value || 0
      };
    });
    
    // If no allocations exist, default visualization to 100%
    if (existingAllocations.length === 0) {
      newForm[0].amount_value = 100;
    }
    
    setAllocationForm(newForm);
    setQuoteSplit(null);
    setQuoteSplitApplied(false);
    setShowAllocationDialog(true);

    // The project was sold on a quote that already priced visualization, the
    // VEXOS setup fee and marketing separately. Offer that split rather than
    // making someone reconstruct it. Read-only: nothing is saved until the
    // person confirms with Save Allocations below.
    apiClient
      .get(`/programs/${programId}/allocations/suggested-from-quote`)
      .then(res => setQuoteSplit(res.data?.available ? res.data : null))
      .catch(() => setQuoteSplit(null));
  };

  const applyQuoteSplit = () => {
    if (!quoteSplit) return;
    setAllocationForm(allocationForm.map(a => {
      const match = (quoteSplit.allocations || []).find(q => q.category === a.category);
      return { ...a, amount_type: "fixed", amount_value: match ? match.amount_value : 0 };
    }));
    setQuoteSplitApplied(true);
  };

  const calculateAllocatedAmount = (alloc) => {
    const contractValue = summary?.contract_value || 0;
    if (alloc.amount_type === "percent") {
      return (alloc.amount_value / 100) * contractValue;
    }
    return alloc.amount_value;
  };

  const getTotalAllocated = () => {
    return allocationForm.reduce((sum, alloc) => sum + calculateAllocatedAmount(alloc), 0);
  };

  const getRemainingToAllocate = () => {
    const contractValue = summary?.contract_value || 0;
    return contractValue - getTotalAllocated();
  };

  const isAllocationBalanced = () => {
    return Math.abs(getRemainingToAllocate()) < 0.01;
  };

  const handleAllocationChange = (index, field, value) => {
    const newForm = [...allocationForm];
    newForm[index] = { ...newForm[index], [field]: value };
    setAllocationForm(newForm);
  };

  const handleSaveAllocations = async () => {
    if (!isAllocationBalanced()) {
      toast.error("Allocations must balance with contract value");
      return;
    }
    
    setSavingAllocations(true);
    try {
      // Filter out zero allocations and send to bulk endpoint
      const allocsToSave = allocationForm.filter(a => a.amount_value > 0);
      await apiClient.put(`/programs/${programId}/allocations/bulk`, allocsToSave);
      toast.success("Contract allocations saved");
      setShowAllocationDialog(false);
      fetchSummary(); // Refresh summary
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save allocations"));
    } finally {
      setSavingAllocations(false);
    }
  };

  const applyAllocationPreset = (preset) => {
    let newForm;
    switch (preset) {
      case "all_viz":
        newForm = allocationForm.map(a => ({ ...a, amount_type: "percent", amount_value: a.category === "visualization" ? 100 : 0 }));
        break;
      case "viz_marketing":
        newForm = allocationForm.map(a => {
          if (a.category === "visualization") return { ...a, amount_type: "percent", amount_value: 80 };
          if (a.category === "marketing") return { ...a, amount_type: "percent", amount_value: 20 };
          return { ...a, amount_type: "percent", amount_value: 0 };
        });
        break;
      case "viz_vexos_marketing":
        newForm = allocationForm.map(a => {
          if (a.category === "visualization") return { ...a, amount_type: "percent", amount_value: 60 };
          if (a.category === "vexos") return { ...a, amount_type: "percent", amount_value: 20 };
          if (a.category === "marketing") return { ...a, amount_type: "percent", amount_value: 20 };
          return { ...a, amount_type: "percent", amount_value: 0 };
        });
        break;
      default:
        return;
    }
    setAllocationForm(newForm);
  };

  // Impact Preview
  const fetchImpactPreview = async (amount) => {
    if (!amount || amount <= 0) {
      setImpactPreview(null);
      return;
    }
    try {
      const res = await apiClient.post(`/costing/impact-preview?program_id=${programId}&amount=${amount}`);
      setImpactPreview(res.data);
    } catch (err) {
      console.error("Failed to fetch impact preview");
    }
  };

  const resetTimeLogForm = () => {
    setTimeLogForm({
      resource_id: "", date: new Date().toISOString().split("T")[0],
      hours_logged: 1, work_type: "other", cost_category: "visualization", notes: ""
    });
  };

  const resetCostItemForm = () => {
    setCostItemForm({
      category: "misc", cost_category: "visualization", vendor_id: "", vendor_name: "", description: "", amount: 0,
      incurred_date: new Date().toISOString().split("T")[0], due_date: "",
      payment_status: "unpaid", invoice_link_url: "",
      has_payment_plan: false, installments: []
    });
  };

  const openEditTimeLog = (log) => {
    setEditingTimeLog(log);
    setTimeLogForm({
      resource_id: log.resource_id,
      date: log.date,
      hours_logged: log.hours_logged || (log.minutes_logged ? log.minutes_logged / 60 : 1),
      work_type: log.work_type,
      cost_category: log.cost_category || "visualization",
      notes: log.notes || ""
    });
    setShowTimeLogDialog(true);
  };

  const openEditCostItem = (item) => {
    setEditingCostItem(item);
    setCostItemForm({
      category: item.category,
      cost_category: item.cost_category || "visualization",
      vendor_id: item.vendor_id || "",
      vendor_name: item.vendor_name || "",
      description: item.description,
      amount: item.amount,
      incurred_date: item.incurred_date,
      due_date: item.due_date || "",
      payment_status: item.payment_status,
      invoice_link_url: item.invoice_link_url || "",
      has_payment_plan: item.has_payment_plan || false,
      installments: item.installments || []
    });
    setShowCostItemDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No costing data available
      </div>
    );
  }

  const healthStyle = HEALTH_STYLES[summary.margin_health] || HEALTH_STYLES.healthy;
  const totalPaid = costItems.reduce((sum, c) => sum + costItemPaid(c), 0);
  const totalUnpaid = costItems.reduce((sum, c) => sum + Math.max(c.amount - costItemPaid(c), 0), 0);
  const planActive = costItemForm.has_payment_plan && costItemForm.installments.length > 0;
  const planNext = planActive ? nextUnpaidInstallment(costItemForm.installments) : null;

  return (
    <div className="space-y-4" data-testid="program-costing-tab">
      {/* Panel A: Profitability Header */}
      <Card className="border-l-4" style={{ borderLeftColor: healthStyle.color }}>
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${healthStyle.bg}`}>
                {summary.margin_health === "healthy" ? (
                  <TrendingUp className="w-5 h-5 text-white" />
                ) : summary.margin_health === "loss" ? (
                  <TrendingDown className="w-5 h-5 text-white" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <Badge className={`${healthStyle.bg} ${healthStyle.text} text-xs`}>
                  {healthStyle.label}
                </Badge>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Target: {summary.target_margin_percent}%
                </p>
              </div>
            </div>
            
            {isAdmin && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowMarginDialog(true)}
                className="text-xs h-7"
              >
                <Settings className="w-3.5 h-3.5 mr-1" />
                Margin Settings
              </Button>
            )}
          </div>
          
          {/* KPIs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {isAdmin && (
              <div className="bg-muted/30 rounded-lg p-2.5">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Contract</p>
                <p className="text-lg font-semibold">{formatCurrency(summary.contract_value)}</p>
              </div>
            )}
            <div className="bg-muted/30 rounded-lg p-2.5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Cost</p>
              <p className="text-lg font-semibold">{formatCurrency(summary.total_cost)}</p>
            </div>
            {isAdmin && (
              <>
                <div className="bg-muted/30 rounded-lg p-2.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Profit</p>
                  <p className={`text-lg font-semibold ${summary.gross_profit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(summary.gross_profit)}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-2.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Margin</p>
                  <p className={`text-lg font-semibold ${summary.gross_margin_percent < summary.target_margin_percent ? 'text-amber-600' : ''}`}>
                    {formatPercent(summary.gross_margin_percent)}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-2.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Budget Left</p>
                  <p className={`text-lg font-semibold ${summary.remaining_cost_budget < 0 ? 'text-red-600' : ''}`}>
                    {formatCurrency(summary.remaining_cost_budget)}
                  </p>
                </div>
              </>
            )}
          </div>
          
          {/* Contract Allocation Summary (inline) */}
          {isAdmin && summary.allocations && summary.allocations.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium">Contract Allocation</span>
                  {!summary.allocation_balanced && (
                    <Badge variant="destructive" className="text-[10px]">Unbalanced</Badge>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={openAllocationDialog}
                  className="text-xs h-6 px-2"
                >
                  <Layers className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {summary.allocations.map(alloc => {
                  const catConfig = ALLOCATION_CATEGORIES.find(c => c.value === alloc.category);
                  return (
                    <div 
                      key={alloc.category}
                      className="flex items-center gap-1.5 px-2 py-1 bg-muted/40 rounded text-xs"
                    >
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: catConfig?.color || '#565350' }}
                      />
                      <span className="text-muted-foreground">{catConfig?.label || alloc.category}:</span>
                      <span className="font-medium">{formatCurrency(alloc.calculated_amount)}</span>
                      {alloc.amount_type === "percent" && (
                        <span className="text-muted-foreground">({alloc.amount_value}%)</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Add Allocation Button (if no allocations yet) */}
          {isAdmin && (!summary.allocations || summary.allocations.length === 0) && summary.contract_value > 0 && (
            <div className="mt-3 pt-3 border-t">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openAllocationDialog}
                className="text-xs h-7"
              >
                <PieChart className="w-3.5 h-3.5 mr-1.5" />
                Allocate Contract
              </Button>
              <p className="text-[10px] text-muted-foreground mt-1">
                Split contract across Visualization, Marketing, and VEXOS
              </p>
            </div>
          )}
          
          {/* Budget Progress Bar */}
          {isAdmin && summary.max_allowed_cost > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="text-muted-foreground">Cost vs Budget</span>
                <span className={summary.remaining_cost_budget < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                  {Math.round((summary.total_cost / summary.max_allowed_cost) * 100)}%
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    summary.remaining_cost_budget < 0 ? 'bg-red-500' :
                    summary.remaining_budget_percent < 20 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(100, (summary.total_cost / summary.max_allowed_cost) * 100)}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Burn Rate Section */}
          {(summary.daily_burn > 0 || summary.runway_to_target !== null) && isAdmin && (
            <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-orange-500" />
                <span className="text-muted-foreground">Burn:</span>
                <span className="font-medium">{formatCurrency(summary.daily_burn)}/day</span>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-muted-foreground">Target:</span>
                <span className={`font-medium ${summary.runway_to_target < 14 ? 'text-amber-600' : ''}`}>
                  {summary.runway_to_target !== null ? `${summary.runway_to_target}d` : "—"}
                </span>
              </div>
              
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">B/E:</span>
                <span className={`font-medium ${summary.runway_to_breakeven < 30 ? 'text-red-600' : ''}`}>
                  {summary.runway_to_breakeven !== null ? `${summary.runway_to_breakeven}d` : "—"}
                </span>
              </div>
              
              <div className="ml-auto">
                <Select value={String(burnWindow)} onValueChange={v => setBurnWindow(parseInt(v))}>
                  <SelectTrigger className="h-6 w-20 text-[10px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Performance Panel (only if allocations exist) */}
      {isAdmin && summary.category_performance && summary.category_performance.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="w-4 h-4 text-blue-500" />
              Category Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-right text-xs">Revenue</TableHead>
                    <TableHead className="text-right text-xs">Cost</TableHead>
                    <TableHead className="text-right text-xs">Profit</TableHead>
                    <TableHead className="text-right text-xs">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.category_performance.map(cp => {
                    const catConfig = ALLOCATION_CATEGORIES.find(c => c.value === cp.category);
                    const isNegative = cp.profit < 0;
                    return (
                      <TableRow key={cp.category}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2.5 h-2.5 rounded-full" 
                              style={{ backgroundColor: catConfig?.color || '#565350' }}
                            />
                            <span className="font-medium text-sm">{catConfig?.label || cp.category}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(cp.allocated_revenue)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(cp.actual_cost)}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${isNegative ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formatCurrency(cp.profit)}
                        </TableCell>
                        <TableCell className={`text-right ${isNegative ? 'text-red-600' : ''}`}>
                          {cp.margin_percent !== null ? `${cp.margin_percent}%` : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Panel B: Labor Cost (Time Logs + Assignment Blocks) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Labor Costs
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({assignmentBlocks.length} scheduled, {timeLogs.length} logged
                {otEntries.length > 0 && `, ${otEntries.length} overtime`})
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="sm" 
                onClick={handleSyncFromTimeline}
                disabled={syncing}
                data-testid="sync-timeline-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Timeline'}
              </Button>
              <Button 
                size="sm" 
                onClick={() => { resetTimeLogForm(); setShowTimeLogDialog(true); }}
                data-testid="add-time-log-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Time
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Assignment Blocks - Scheduled Time */}
          {assignmentBlocks.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Scheduled Time (from Artist Timeline)
              </p>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-blue-50 dark:bg-blue-950/30">
                      <TableHead>Date</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead>Work Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      {isAdmin && <TableHead className="text-right">Rate</TableHead>}
                      {isAdmin && <TableHead className="text-right">Cost</TableHead>}
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllBlocks ? assignmentBlocks : assignmentBlocks.slice(0, LABOR_ROWS_SHOWN)).map(block => {
                      const catConfig = ALLOCATION_CATEGORIES.find(c => c.value === (block.cost_category || 'visualization'));
                      return (
                        <TableRow key={block.id}>
                          <TableCell className="text-sm whitespace-nowrap">
                            {block.start_date}
                          </TableCell>
                          <TableCell>
                            {(!block.resource_name || block.resource_name === "Unknown Resource" || block.resource_name === "Unknown") ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-amber-600">Unknown Resource</span>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1.5 text-xs text-blue-600 hover:text-blue-800"
                                    onClick={() => openReassignDialog(block.resource_id)}
                                    data-testid={`reassign-block-${block.id}`}
                                  >
                                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                                    Re-assign
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="font-medium">{block.resource_name}</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate">{block.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {block.work_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isAdmin ? (
                              <Select
                                value={block.cost_category || "visualization"}
                                onValueChange={(value) => handleUpdateBlockCategory(block.id, value)}
                              >
                                <SelectTrigger className="h-7 w-28 text-xs" data-testid={`block-category-${block.id}`}>
                                  <SelectValue>
                                    <div className="flex items-center gap-1.5">
                                      <div 
                                        className="w-2 h-2 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: catConfig?.color || '#565350' }}
                                      />
                                      <span>{catConfig?.label || 'Visualization'}</span>
                                    </div>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {ALLOCATION_CATEGORIES.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                      <div className="flex items-center gap-1.5">
                                        <div 
                                          className="w-2 h-2 rounded-full" 
                                          style={{ backgroundColor: cat.color }}
                                        />
                                        {cat.label}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: catConfig?.color || '#565350' }}
                                />
                                <span className="text-xs">{catConfig?.label || 'Visualization'}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">{block.hours_total}h</TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              {block.rate_missing ? (
                                <Badge variant="destructive" className="text-[10px]">Missing</Badge>
                              ) : (
                                `${formatCurrency(block.hourly_rate)}/h`
                              )}
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell className="text-right font-medium">
                              {block.rate_missing ? "—" : formatCurrency(block.computed_cost)}
                            </TableCell>
                          )}
                          <TableCell>
                            <Badge 
                              className={`text-[10px] ${
                                block.status === "In Progress" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" :
                                block.status === "Confirmed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" :
                                "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                              }`}
                            >
                              {block.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <ShowMoreFooter
                  hidden={assignmentBlocks.length - LABOR_ROWS_SHOWN}
                  expanded={showAllBlocks}
                  onToggle={() => setShowAllBlocks(v => !v)}
                  testId="show-more-blocks"
                />
              </div>
            </div>
          )}

          {/* Manual Time Logs */}
          <div>
            {assignmentBlocks.length > 0 && (
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Manual Time Entries
              </p>
            )}
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Date</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Work Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    {isAdmin && <TableHead className="text-right">Rate</TableHead>}
                    {isAdmin && <TableHead className="text-right">Cost</TableHead>}
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 9 : 6} className="text-center py-6 text-muted-foreground">
                        No manual time logs yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    (showAllLogs ? timeLogs : timeLogs.slice(0, LABOR_ROWS_SHOWN)).map(log => {
                      const catConfig = ALLOCATION_CATEGORIES.find(c => c.value === (log.cost_category || 'visualization'));
                      return (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">{log.date}</TableCell>
                          <TableCell>
                            {(!log.resource_name || log.resource_name === "Unknown Resource" || log.resource_name === "Unknown") ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-amber-600">Unknown Resource</span>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1.5 text-xs text-blue-600 hover:text-blue-800"
                                    onClick={() => openReassignDialog(log.resource_id)}
                                    data-testid={`reassign-log-${log.id}`}
                                  >
                                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                                    Re-assign
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="font-medium">{log.resource_name}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {log.work_type?.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: catConfig?.color || '#565350' }}
                              />
                              <span className="text-xs">{catConfig?.label || 'Visualization'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">{log.hours_logged}h</TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              {log.rate_missing ? (
                                <Badge variant="destructive" className="text-[10px]">Missing</Badge>
                              ) : (
                                `${formatCurrency(log.hourly_rate)}/h`
                              )}
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell className="text-right font-medium">
                              {log.rate_missing ? "—" : formatCurrency(log.computed_cost)}
                            </TableCell>
                          )}
                          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                            {log.notes || "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditTimeLog(log)}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTimeLog(log.id)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  )}
                </TableBody>
              </Table>
              <ShowMoreFooter
                hidden={timeLogs.length - LABOR_ROWS_SHOWN}
                expanded={showAllLogs}
                onToggle={() => setShowAllLogs(v => !v)}
                testId="show-more-logs"
              />
            </div>
          </div>

          {/* Overtime booked on the Artist Timeline. Read-only: it is created and
              edited there, and shows here so it is visible in the project's cost. */}
          {otEntries.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Overtime (from Artist Timeline)
              </p>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50 dark:bg-amber-950/30">
                      <TableHead>Date</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Deliverable</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      {isAdmin && <TableHead className="text-right">Rate</TableHead>}
                      {isAdmin && <TableHead className="text-right">Cost</TableHead>}
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllOt ? otEntries : otEntries.slice(0, LABOR_ROWS_SHOWN)).map(ot => (
                      <TableRow key={ot.id} data-testid={`ot-row-${ot.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">{ot.date}</TableCell>
                        <TableCell className="font-medium">{ot.artist_name || "—"}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{ot.deliverable_label || "—"}</TableCell>
                        <TableCell className="text-right font-medium">{ot.hours}h</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">{formatCurrency(ot.rate)}/h</TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right font-medium">{formatCurrency(ot.total_cost)}</TableCell>
                        )}
                        <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                          {ot.notes || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ShowMoreFooter
                  hidden={otEntries.length - LABOR_ROWS_SHOWN}
                  expanded={showAllOt}
                  onToggle={() => setShowAllOt(v => !v)}
                  testId="show-more-ot"
                />
              </div>
            </div>
          )}

          {/* Always the full figure, whatever the tables above are showing —
              it comes off the server summary, not the visible rows. */}
          {isAdmin && (timeLogs.length > 0 || assignmentBlocks.length > 0 || otEntries.length > 0) && (
            <div className="mt-3 flex justify-end">
              <div className="text-sm">
                {summary.total_overtime_cost > 0 && (
                  <span className="text-muted-foreground mr-4">
                    incl. overtime {formatCurrency(summary.total_overtime_cost)}
                  </span>
                )}
                <span className="text-muted-foreground">Total Labor:</span>
                <span className="font-bold ml-2">{formatCurrency(summary.total_labor_cost)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Panel C: Vendor / Other Costs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-5 h-5 text-purple-600" />
              Vendor & Other Costs
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => { resetCostItemForm(); setShowCostItemDialog(true); }}
              data-testid="add-cost-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Cost
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                      No cost items yet
                    </TableCell>
                  </TableRow>
                ) : (
                  costItems.map(item => (
                    <Fragment key={item.id}>
                    <TableRow className={item.has_payment_plan && item.installments?.length ? "border-b-0" : ""}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {item.category_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{item.vendor_name || "—"}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{item.description}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                      <TableCell className="text-sm">{item.incurred_date}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge 
                            className={`text-[10px] ${
                              item.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                              item.payment_status === "partially_paid" ? "bg-blue-100 text-blue-700" :
                              item.payment_status === "disputed" ? "bg-red-100 text-red-700" :
                              "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {item.payment_status.replace("_", " ")}
                          </Badge>
                          {item.has_payment_plan && (
                            <Badge variant="outline" className="text-[9px] ml-1" title="Has payment plan">
                              <CreditCard className="w-2.5 h-2.5 mr-0.5" />
                              {item.installments?.length || 0}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.invoice_link_url ? (
                          <a 
                            href={item.invoice_link_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Link
                          </a>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {item.payment_status !== "paid" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleMarkPaid(item)}
                              title={item.has_payment_plan && item.installments?.length
                                ? `Mark "${nextUnpaidInstallment(item.installments)?.label || "next payment"}" paid`
                                : "Mark as Paid"}
                            >
                              <Check className="w-3 h-3 text-emerald-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditCostItem(item)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCostItem(item.id)}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {item.has_payment_plan && item.installments?.length > 0 && (
                      <TableRow className="hover:bg-transparent" data-testid={`cost-item-plan-${item.id}`}>
                        <TableCell colSpan={8} className="pt-0 pb-3">
                          <div className="flex flex-wrap gap-2 pl-1">
                            {item.installments.map((inst, idx) => (
                              <div
                                key={inst.id || idx}
                                className={`flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${inst.paid ? "text-muted-foreground" : ""}`}
                              >
                                {inst.paid ? (
                                  <Check className="w-3 h-3 text-emerald-600" />
                                ) : (
                                  <Clock className="w-3 h-3 text-amber-600" />
                                )}
                                <span className="font-medium">{inst.label || `Payment ${idx + 1}`}</span>
                                <span>{formatCurrency(inst.amount)}</span>
                                <span className="text-muted-foreground">
                                  {inst.paid
                                    ? `paid ${formatShortDate(inst.paid_date)}`
                                    : inst.due_date ? `due ${formatShortDate(inst.due_date)}` : "no due date"}
                                </span>
                                {!inst.paid && (
                                  <button
                                    type="button"
                                    className="text-emerald-700 hover:underline"
                                    onClick={() => handleMarkPaid(item, inst)}
                                  >
                                    Mark paid
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {costItems.length > 0 && (
            <div className="mt-3 flex justify-end gap-6">
              <div className="text-sm">
                <span className="text-muted-foreground">Paid:</span>
                <span className="font-medium text-emerald-600 ml-2">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Unpaid:</span>
                <span className="font-medium text-amber-600 ml-2">{formatCurrency(totalUnpaid)}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-bold ml-2">{formatCurrency(summary.total_vendor_cost)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Panel D: Commissions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Commissions
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({commissions.length} total)
              </span>
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => { resetCommissionForm(); setShowCommissionDialog(true); }}
              data-testid="add-commission-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Commission
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50">
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {isAdmin && <TableHead className="text-right">%</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-6 text-muted-foreground">
                      No commissions yet
                    </TableCell>
                  </TableRow>
                ) : (
                  commissions.map(commission => (
                    <TableRow key={commission.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-3.5 h-3.5 text-green-600" />
                          <div>
                            <p className="font-medium">{commission.user_name || "Unknown"}</p>
                            <p className="text-[10px] text-muted-foreground">{commission.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {commission.commission_type?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">{commission.description}</TableCell>
                      <TableCell className="text-right font-medium text-green-700">
                        {formatCurrency(commission.amount)}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          {commission.percentage ? (
                            <span className="flex items-center justify-end gap-0.5">
                              <Percent className="w-3 h-3 text-muted-foreground" />
                              {commission.percentage}
                            </span>
                          ) : "—"}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge 
                          className={`text-[10px] ${
                            commission.payment_status === "paid" 
                              ? "bg-emerald-100 text-emerald-700" 
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {commission.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">
                        {commission.notes || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {commission.payment_status !== "paid" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleMarkCommissionPaid(commission)}
                              title="Mark as Paid"
                            >
                              <Check className="w-3 h-3 text-emerald-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditCommission(commission)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteCommission(commission.id)}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {commissions.length > 0 && (
            <div className="mt-3 flex justify-end gap-6">
              <div className="text-sm">
                <span className="text-muted-foreground">Paid:</span>
                <span className="font-medium text-emerald-600 ml-2">
                  {formatCurrency(commissions.filter(c => c.payment_status === "paid").reduce((sum, c) => sum + c.amount, 0))}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Unpaid:</span>
                <span className="font-medium text-amber-600 ml-2">
                  {formatCurrency(commissions.filter(c => c.payment_status !== "paid").reduce((sum, c) => sum + c.amount, 0))}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-bold ml-2">{formatCurrency(summary.total_commission_cost)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Log Dialog */}
      <Dialog open={showTimeLogDialog} onOpenChange={setShowTimeLogDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTimeLog ? "Edit Time Log" : "Add Time Log"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Resource *</Label>
              <Select 
                value={timeLogForm.resource_id} 
                onValueChange={v => setTimeLogForm({ ...timeLogForm, resource_id: v })}
              >
                <SelectTrigger data-testid="time-log-resource-select">
                  <SelectValue placeholder="Select resource" />
                </SelectTrigger>
                <SelectContent>
                  {resources.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={timeLogForm.date}
                  onChange={e => setTimeLogForm({ ...timeLogForm, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hours *</Label>
                <Input 
                  type="number" 
                  step="0.5"
                  min="0.5"
                  value={timeLogForm.hours_logged}
                  onChange={e => setTimeLogForm({ ...timeLogForm, hours_logged: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Work Type</Label>
              <Select 
                value={timeLogForm.work_type} 
                onValueChange={v => setTimeLogForm({ ...timeLogForm, work_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config?.work_types?.map(wt => (
                    <SelectItem key={wt} value={wt} className="capitalize">
                      {wt.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Cost Category</Label>
              <Select 
                value={timeLogForm.cost_category} 
                onValueChange={v => setTimeLogForm({ ...timeLogForm, cost_category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALLOCATION_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={timeLogForm.notes}
                onChange={e => setTimeLogForm({ ...timeLogForm, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeLogDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTimeLog} disabled={!timeLogForm.resource_id || !timeLogForm.date}>
              {editingTimeLog ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost Item Dialog */}
      <Dialog open={showCostItemDialog} onOpenChange={setShowCostItemDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCostItem ? "Edit Cost Item" : "Add Cost Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={costItemForm.category} 
                onValueChange={v => setCostItemForm({ ...costItemForm, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config?.cost_categories?.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cost Category (for allocation)</Label>
                <Select 
                  value={costItemForm.cost_category} 
                  onValueChange={v => setCostItemForm({ ...costItemForm, cost_category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOCATION_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Select 
                  value={costItemForm.vendor_id || "none"}
                  onValueChange={v => {
                    if (v === "none") {
                      setCostItemForm({ ...costItemForm, vendor_id: "", vendor_name: "" });
                    } else {
                      const vendor = vendors.find(vn => vn.id === v);
                      setCostItemForm({ 
                        ...costItemForm, 
                        vendor_id: v, 
                        vendor_name: vendor?.name || "" 
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="vendor-selector">
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">No vendor</span>
                    </SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-purple-500" />
                          {v.name}
                          <span className="text-xs text-muted-foreground">({v.type})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vendors.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No vendors yet. <a href="/studio/vendors" className="text-blue-600 hover:underline">Add vendors</a>
                  </p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input 
                value={costItemForm.description}
                onChange={e => setCostItemForm({ ...costItemForm, description: e.target.value })}
                placeholder="What is this cost for?"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input 
                  type="number" 
                  value={costItemForm.amount}
                  onChange={e => {
                    const amount = parseFloat(e.target.value) || 0;
                    setCostItemForm({
                      ...costItemForm,
                      amount,
                      installments: rescaleInstallments(costItemForm.installments, amount)
                    });
                    if (!editingCostItem) {
                      fetchImpactPreview(amount);
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Status</Label>
                {planActive ? (
                  <Select
                    value={costItemForm.payment_status === "disputed" ? "disputed" : "plan"}
                    onValueChange={v => setCostItemForm({
                      ...costItemForm,
                      payment_status: v === "disputed" ? "disputed" : derivePlanStatus(costItemForm.installments)
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plan">
                        Per plan · {derivePlanStatus(costItemForm.installments).replace("_", " ")}
                      </SelectItem>
                      <SelectItem value="disputed">Disputed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                <Select 
                  value={costItemForm.payment_status} 
                  onValueChange={v => setCostItemForm({ ...costItemForm, payment_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="disputed">Disputed</SelectItem>
                  </SelectContent>
                </Select>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Incurred Date *</Label>
                <Input 
                  type="date" 
                  value={costItemForm.incurred_date}
                  onChange={e => setCostItemForm({ ...costItemForm, incurred_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{planActive ? "Next Payment Due" : "Due Date"}</Label>
                {planActive ? (
                  <div className="h-9 flex items-center text-sm text-muted-foreground">
                    {planNext
                      ? (planNext.due_date ? formatShortDate(planNext.due_date) : "No date set") 
                      : "All paid"}
                  </div>
                ) : (
                <Input 
                  type="date" 
                  value={costItemForm.due_date}
                  onChange={e => setCostItemForm({ ...costItemForm, due_date: e.target.value })}
                />
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Invoice Link</Label>
              <Input 
                value={costItemForm.invoice_link_url}
                onChange={e => setCostItemForm({ ...costItemForm, invoice_link_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            {/* Payment Plan: split the cost into labelled, dated installments */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Payment Plan</Label>
                </div>
                <Switch
                  checked={costItemForm.has_payment_plan}
                  onCheckedChange={(checked) => setCostItemForm({ ...costItemForm, has_payment_plan: checked })}
                  data-testid="payment-plan-toggle"
                />
              </div>
              {costItemForm.has_payment_plan && (
                <VendorPaymentPlanEditor
                  installments={costItemForm.installments}
                  total={costItemForm.amount}
                  firstDueDate={costItemForm.due_date || costItemForm.incurred_date}
                  onChange={installments => setCostItemForm(f => ({
                    ...f,
                    installments,
                    payment_status: f.payment_status === "disputed" ? "disputed" : derivePlanStatus(installments, f.payment_status)
                  }))}
                />
              )}
            </div>
            
            {/* Impact Preview (Guardrail) */}
            {impactPreview && !editingCostItem && (
              <div className={`p-4 rounded-lg border-2 ${
                impactPreview.crosses_threshold ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50'
              }`}>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 ${impactPreview.crosses_threshold ? 'text-amber-600' : 'text-gray-500'}`} />
                  Impact Preview
                </h4>
                {impactPreview.warning_message && (
                  <p className="text-amber-700 text-sm font-medium mb-2">{impactPreview.warning_message}</p>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Cost:</span>
                    <span className="ml-2">{formatCurrency(impactPreview.before_total_cost)} → </span>
                    <span className="font-medium">{formatCurrency(impactPreview.after_total_cost)}</span>
                  </div>
                  {isAdmin && impactPreview.after_margin_percent !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Margin:</span>
                      <span className="ml-2">{formatPercent(impactPreview.before_margin_percent)} → </span>
                      <span className={`font-medium ${impactPreview.after_margin_percent < 0 ? 'text-red-600' : ''}`}>
                        {formatPercent(impactPreview.after_margin_percent)}
                      </span>
                    </div>
                  )}
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Status:</span>
                    <Badge className={`ml-2 ${HEALTH_STYLES[impactPreview.before_status]?.bg} ${HEALTH_STYLES[impactPreview.before_status]?.text}`}>
                      {HEALTH_STYLES[impactPreview.before_status]?.label}
                    </Badge>
                    <span className="mx-2">→</span>
                    <Badge className={`${HEALTH_STYLES[impactPreview.after_status]?.bg} ${HEALTH_STYLES[impactPreview.after_status]?.text}`}>
                      {HEALTH_STYLES[impactPreview.after_status]?.label}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCostItemDialog(false); setImpactPreview(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCostItem} 
              disabled={!costItemForm.description || !costItemForm.incurred_date || costItemForm.amount <= 0}
            >
              {editingCostItem ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Margin Settings Dialog */}
      <Dialog open={showMarginDialog} onOpenChange={setShowMarginDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Margin Target Settings
            </DialogTitle>
            <DialogDescription>
              Set the target profit margin for this program (50% - 90%)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Target Margin</Label>
                <span className="text-2xl font-light text-primary">{marginForm.target_margin_percent}%</span>
              </div>
              <Slider
                value={[marginForm.target_margin_percent]}
                onValueChange={([v]) => setMarginForm({ ...marginForm, target_margin_percent: v })}
                min={50}
                max={90}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>50%</span>
                <span>60%</span>
                <span>70%</span>
                <span>80%</span>
                <span>90%</span>
              </div>
              
              {summary && (
                <div className="text-sm bg-blue-50 p-4 rounded-lg mt-4 space-y-2">
                  <p className="font-medium text-blue-800">Impact Preview</p>
                  <div className="grid grid-cols-2 gap-4 text-blue-700">
                    <div>
                      <p className="text-xs text-blue-600">Max Allowed Cost</p>
                      <p className="font-semibold">{formatCurrency(summary.contract_value * (1 - marginForm.target_margin_percent / 100))}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Min Required Profit</p>
                      <p className="font-semibold">{formatCurrency(summary.contract_value * marginForm.target_margin_percent / 100)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarginDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveMargin}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              {editingCommission ? "Edit Commission" : "Add Commission"}
            </DialogTitle>
            <DialogDescription>
              {editingCommission 
                ? "Update commission details for this team member" 
                : "Add a new commission for a team member on this project"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient *</Label>
              <Select 
                value={commissionForm.user_id} 
                onValueChange={v => setCommissionForm({ ...commissionForm, user_id: v })}
              >
                <SelectTrigger data-testid="commission-recipient-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id || u.user_id} value={u.id || u.user_id}>
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-3 h-3 text-green-500" />
                        {u.name}
                        <span className="text-xs text-muted-foreground">({u.role})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Commission Type *</Label>
                <Select 
                  value={commissionForm.commission_type} 
                  onValueChange={v => setCommissionForm({ ...commissionForm, commission_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="finder">Finder's Fee</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input 
                  type="number" 
                  value={commissionForm.amount}
                  onChange={e => setCommissionForm({ ...commissionForm, amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input 
                value={commissionForm.description}
                onChange={e => setCommissionForm({ ...commissionForm, description: e.target.value })}
                placeholder="What is this commission for?"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Percentage (optional)</Label>
                <Input 
                  type="number" 
                  step="0.1"
                  value={commissionForm.percentage || ""}
                  onChange={e => setCommissionForm({ ...commissionForm, percentage: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="e.g. 5"
                />
                <p className="text-[10px] text-muted-foreground">If calculated as % of contract</p>
              </div>
              <div className="space-y-2">
                <Label>Payment Status</Label>
                <Select 
                  value={commissionForm.payment_status} 
                  onValueChange={v => setCommissionForm({ ...commissionForm, payment_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {commissionForm.payment_status === "paid" && (
              <div className="space-y-2">
                <Label>Paid Date</Label>
                <Input 
                  type="date" 
                  value={commissionForm.paid_date}
                  onChange={e => setCommissionForm({ ...commissionForm, paid_date: e.target.value })}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={commissionForm.notes}
                onChange={e => setCommissionForm({ ...commissionForm, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCommissionDialog(false); setEditingCommission(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCommission} 
              disabled={!commissionForm.user_id || !commissionForm.description || commissionForm.amount <= 0}
            >
              {editingCommission ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Allocation Dialog */}
      <Dialog open={showAllocationDialog} onOpenChange={setShowAllocationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />
              Allocate Contract Value
            </DialogTitle>
            <DialogDescription>
              Split the contract value across different categories for profitability tracking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Contract Value Summary */}
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Contract Value</span>
                <span className="text-lg font-bold">{formatCurrency(summary?.contract_value || 0)}</span>
              </div>
            </div>
            
            {/* Quick Presets */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Quick presets:</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => applyAllocationPreset("all_viz")}
              >
                100% Viz
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => applyAllocationPreset("viz_marketing")}
              >
                80/20 Viz+Mkt
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => applyAllocationPreset("viz_vexos_marketing")}
              >
                60/20/20 Split
              </Button>
              {quoteSplit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300"
                  onClick={applyQuoteSplit}
                  data-testid="apply-quote-split"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  From the quote
                </Button>
              )}
            </div>

            {/* What the quote proposes, and where it doesn't reach ──────────
                Filling the fields is not saving them: the numbers land in the
                inputs below and Save Allocations is still a deliberate click. */}
            {quoteSplit && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/20 p-3 space-y-1">
                <p className="text-xs">
                  {quoteSplitApplied ? (
                    <span className="font-medium">Filled in from the linked quote — review and save.</span>
                  ) : (
                    <>The linked quote splits {formatCurrency(quoteSplit.quote_one_time_total)} of
                    one-time work. <span className="font-medium">From the quote</span> fills these
                    fields in for you.</>
                  )}
                </p>
                {quoteSplit.basis === "surplus" && (
                  <p className="text-[11px] text-muted-foreground">
                    This project is worth {formatCurrency(quoteSplit.difference)} more than its
                    quote — usually change orders. That amount goes to <strong>Other</strong>;
                    move it if you know which category earned it.
                  </p>
                )}
                {quoteSplit.basis === "scaled" && (
                  <p className="text-[11px] text-muted-foreground">
                    The contract value is {formatCurrency(Math.abs(quoteSplit.difference))} below
                    the quote, so every category is scaled down by the same factor.
                  </p>
                )}
                {quoteSplit.recurring_excluded > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    The {formatCurrency(quoteSplit.recurring_excluded)} platform subscription is
                    not included — it bills on its own ledger and is not part of contract value.
                  </p>
                )}
              </div>
            )}

            {/* Allocation Inputs */}
            <div className="space-y-3">
              {allocationForm.map((alloc, index) => {
                const catConfig = ALLOCATION_CATEGORIES.find(c => c.value === alloc.category);
                const calculatedAmount = calculateAllocatedAmount(alloc);
                
                return (
                  <div 
                    key={alloc.category}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-background"
                  >
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: catConfig?.color || '#565350' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{catConfig?.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(calculatedAmount)}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Select
                        value={alloc.amount_type}
                        onValueChange={(v) => handleAllocationChange(index, "amount_type", v)}
                      >
                        <SelectTrigger className="w-24 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percent</SelectItem>
                          <SelectItem value="fixed">Fixed $</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Input
                        type="number"
                        step={alloc.amount_type === "percent" ? "1" : "100"}
                        min="0"
                        max={alloc.amount_type === "percent" ? "100" : undefined}
                        className="w-24 h-8 text-sm"
                        value={alloc.amount_value}
                        onChange={(e) => handleAllocationChange(index, "amount_value", parseFloat(e.target.value) || 0)}
                      />
                      
                      {alloc.amount_type === "percent" && (
                        <span className="text-sm text-muted-foreground">%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Balance Summary */}
            <div className={`rounded-lg p-3 ${isAllocationBalanced() ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Allocated</p>
                  <p className="text-lg font-bold">{formatCurrency(getTotalAllocated())}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Remaining</p>
                  <p className={`text-lg font-bold ${isAllocationBalanced() ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {formatCurrency(getRemainingToAllocate())}
                  </p>
                </div>
              </div>
              
              {!isAllocationBalanced() && (
                <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Allocations must equal contract value to save
                </p>
              )}
              
              {isAllocationBalanced() && (
                <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Allocations balanced
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAllocationDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAllocations}
              disabled={!isAllocationBalanced() || savingAllocations}
              data-testid="save-allocations-btn"
            >
              {savingAllocations ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Allocations"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Resource Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="max-w-md" data-testid="reassign-resource-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Re-assign Unknown Resource
            </DialogTitle>
            <DialogDescription>
              Select the correct resource to link these orphaned cost entries to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Old Resource ID</Label>
              <p className="text-sm font-mono bg-muted px-2 py-1 rounded mt-1 break-all" data-testid="reassign-old-id">{reassignOldId}</p>
            </div>
            <div>
              <Label>Assign to Resource</Label>
              <Select value={reassignNewId} onValueChange={setReassignNewId}>
                <SelectTrigger className="mt-1" data-testid="reassign-resource-select">
                  <SelectValue placeholder="Select a resource..." />
                </SelectTrigger>
                <SelectContent>
                  {resources.filter(r => !r.is_archived).map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.display_name} ({r.resource_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReassignResource}
              disabled={!reassignNewId || reassigning}
              data-testid="reassign-confirm-btn"
            >
              {reassigning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Reassigning...
                </>
              ) : (
                "Re-assign"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
